<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useAlert } from 'dashboard/composables';
import { useQuepasaChannelApi } from '../composables/useQuepasaChannelApi';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const { endpoint, http } = useQuepasaChannelApi(() => props.inbox);
const ignoredRecipients = ref([]);
const sources = ref({ groups: [], contacts: [] });
const selectedRecipientId = ref('');
const isLoading = ref(false);
const isSaving = ref(false);
const removingId = ref('');

const availableRecipients = computed(() => {
  const ignoredIds = new Set(
    ignoredRecipients.value.map(recipient => recipient.recipient_id)
  );

  return [...sources.value.groups, ...sources.value.contacts]
    .filter(recipient => !ignoredIds.has(recipient.recipient_id))
    .sort((left, right) =>
      left.display_name.localeCompare(right.display_name, undefined, {
        sensitivity: 'base',
      })
    );
});

const selectedRecipient = computed(() =>
  availableRecipients.value.find(
    recipient => recipient.recipient_id === selectedRecipientId.value
  )
);

const load = async () => {
  isLoading.value = true;
  try {
    const [ignoredResponse, sourcesResponse] = await Promise.all([
      http.get(endpoint('/ignored_recipients')),
      http.get(endpoint('/ignored_recipients/sources')),
    ]);
    ignoredRecipients.value = ignoredResponse.data?.ignored_recipients || [];
    sources.value = {
      groups: sourcesResponse.data?.groups || [],
      contacts: sourcesResponse.data?.contacts || [],
    };
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.IGNORED_RECIPIENTS.LOAD_ERROR')
    );
  } finally {
    isLoading.value = false;
  }
};

const addRecipient = async () => {
  if (!selectedRecipient.value) return;

  isSaving.value = true;
  try {
    const { data } = await http.post(endpoint('/ignored_recipients'), {
      recipient_id: selectedRecipient.value.recipient_id,
      display_name: selectedRecipient.value.display_name,
    });
    ignoredRecipients.value = [
      data.ignored_recipient,
      ...ignoredRecipients.value,
    ];
    selectedRecipientId.value = '';
    useAlert(t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.IGNORED_RECIPIENTS.SAVED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.IGNORED_RECIPIENTS.SAVE_ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

const removeRecipient = async recipient => {
  removingId.value = recipient.id;
  try {
    await http.delete(endpoint(`/ignored_recipients/${recipient.id}`));
    ignoredRecipients.value = ignoredRecipients.value.filter(
      item => item.id !== recipient.id
    );
    useAlert(t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.IGNORED_RECIPIENTS.REMOVED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.IGNORED_RECIPIENTS.REMOVE_ERROR')
    );
  } finally {
    removingId.value = '';
  }
};

const recipientTypeLabel = recipient => {
  if (recipient.recipient_type === 'group') {
    return t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.IGNORED_RECIPIENTS.GROUP');
  }

  return t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.IGNORED_RECIPIENTS.CONTACT');
};

onMounted(load);
</script>

<template>
  <div class="flex flex-col gap-4">
    <p class="mb-0 text-sm text-n-slate-11">
      {{ t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.IGNORED_RECIPIENTS.HELP') }}
    </p>

    <div class="flex flex-col gap-2 sm:flex-row">
      <select
        v-model="selectedRecipientId"
        :disabled="isLoading || isSaving"
        class="w-full rounded-lg border border-n-slate-6 bg-n-background px-3 py-2 text-sm text-n-slate-12"
        data-target="quepasa-channel-ignored-recipient-select"
      >
        <option value="">
          {{ t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.IGNORED_RECIPIENTS.SELECT') }}
        </option>
        <option
          v-for="recipient in availableRecipients"
          :key="recipient.recipient_id"
          :value="recipient.recipient_id"
        >
          {{
            t(
              'QUEPASA_CHANNEL.SETTINGS.OPTIONS.IGNORED_RECIPIENTS.SELECT_OPTION',
              {
                name: recipient.display_name,
                type: recipientTypeLabel(recipient),
              }
            )
          }}
        </option>
      </select>
      <NextButton
        icon="i-lucide-ban"
        color-scheme="secondary"
        :label="t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.IGNORED_RECIPIENTS.ADD')"
        :is-loading="isSaving"
        :disabled="!selectedRecipient || isLoading"
        data-target="quepasa-channel-ignored-recipient-add"
        @click="addRecipient"
      />
    </div>

    <p
      v-if="!isLoading && !ignoredRecipients.length"
      class="mb-0 text-sm text-n-slate-11"
    >
      {{ t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.IGNORED_RECIPIENTS.EMPTY') }}
    </p>

    <ul v-else class="m-0 flex list-none flex-col gap-2 p-0">
      <li
        v-for="recipient in ignoredRecipients"
        :key="recipient.id"
        class="flex items-center gap-3 rounded-lg border border-n-slate-6 px-3 py-2"
      >
        <span class="min-w-0 flex-1">
          <span class="block truncate text-sm font-medium text-n-slate-12">
            {{ recipient.display_name || recipient.recipient_id }}
          </span>
          <span class="block truncate text-xs text-n-slate-11">
            {{ recipientTypeLabel(recipient) }}
          </span>
          <span class="block truncate text-xs text-n-slate-11">
            {{ recipient.recipient_id }}
          </span>
        </span>
        <NextButton
          icon="i-lucide-x"
          color-scheme="secondary"
          size="sm"
          :label="
            t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.IGNORED_RECIPIENTS.REMOVE')
          "
          :is-loading="removingId === recipient.id"
          :disabled="Boolean(removingId)"
          @click="removeRecipient(recipient)"
        />
      </li>
    </ul>
  </div>
</template>
